import React, { useEffect, useState } from "react";
import { Edit, Trash2, Eye, Download, FileText, ToggleLeft, ToggleRight } from "lucide-react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import "./Customers.css";

const url = process.env.REACT_APP_BACKEND_URL;

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [houseTypes, setHouseTypes] = useState([]);
  const [zones, setZones] = useState([]);
  const [collectors, setCollectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterHouseType, setFilterHouseType] = useState("");
  const [filterHouseNo, setFilterHouseNo] = useState("");
  const [filterZone, setFilterZone] = useState("");
  const [filterCollector, setFilterCollector] = useState("");
  const [filterStatus, setFilterStatus] = useState(""); // active/inactive filter
  const [filterOwner, setFilterOwner] = useState(""); // yes/no filter
  const navigate = useNavigate();

  // ✅ Fetch customers
  const fetchCustomers = async () => {
    try {
      const res = await axios.get(`${url}/api/customer`);
      console.log("📥 Customers data:", res.data);
      setCustomers(res.data.data || []);
    } catch (err) {
      alert(err.response?.data?.message || "Failed to fetch customers");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Fetch house types
  const fetchHouseTypes = async () => {
    try {
      const res = await axios.get(`${url}/api/housetype`);
      setHouseTypes(res.data.data || []);
    } catch (err) {
      console.error("Failed to fetch house types");
    }
  };

  // ✅ Fetch zones
  const fetchZones = async () => {
    try {
      const res = await axios.get(`${url}/api/zone`);
      setZones(res.data.data || []);
    } catch (err) {
      console.error("Failed to fetch zones");
    }
  };

  // ✅ Fetch collectors
  const fetchCollectors = async () => {
    try {
      const res = await axios.get(`${url}/api/collectors`);
      setCollectors(res.data.data || []);
    } catch (err) {
      console.error("Failed to fetch collectors");
    }
  };

  useEffect(() => {
    Promise.all([
      fetchCustomers(),
      fetchHouseTypes(),
      fetchZones(),
      fetchCollectors()
    ]);
  }, []);

  // ✅ Toggle customer status (active/inactive)
  const toggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    const action = newStatus === "active" ? "activate" : "deactivate";
    
    if (window.confirm(`Are you sure you want to ${action} this customer?`)) {
      try {
        // You'll need to create these endpoints in your backend
        await axios.put(`${url}/api/customer/${id}/${action}`);
        fetchCustomers();
        alert(`Customer ${action}d successfully`);
      } catch (err) {
        alert(err.response?.data?.message || `Failed to ${action} customer`);
      }
    }
  };

  // ✅ Delete customer (permanent delete)
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to permanently delete this customer?")) {
      try {
        await axios.delete(`${url}/api/customer/${id}`);
        fetchCustomers();
        alert("Customer deleted successfully");
      } catch (err) {
        alert(err.response?.data?.message || "Failed to delete customer");
      }
    }
  };

  // ✅ Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  // ✅ Format date for Excel
  const formatDateForExcel = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // ✅ Export to Excel
  const exportToExcel = () => {
    const excelData = filteredCustomers.map((c, index) => ({
      "S.No": index + 1,
      "House No": `D.W-${c.houseNo}`,
      "Customer Name": c.customerName || "N/A",
      "Phone": c.phone || "N/A",
      "Rate": c.rate ? `$${c.rate}` : "N/A",
      "Note": c.note || "-",
      "House Type": c.houseType?.name || "N/A",
      "Zone": c.zone?.name || "N/A",
      "Collector": c.collector ? `${c.collector.fname || ""} ${c.collector.lname || ""}`.trim() : "N/A",
      "Owner": c.isOwner ? "Yes" : "No",
      "Status": c.status || "active",
      "Created At": formatDateForExcel(c.createdAt)
    }));

    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Customers");
    
    const date = new Date();
    const fileName = `customers_${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // ✅ Export Summary
  const exportSummary = () => {
    const ownerCount = filteredCustomers.filter(c => c.isOwner === true).length;
    const nonOwnerCount = filteredCustomers.filter(c => c.isOwner === false).length;
    const activeCount = filteredCustomers.filter(c => c.status === "active").length;
    const inactiveCount = filteredCustomers.filter(c => c.status === "inactive").length;
    
    const summaryData = [{
      "Report Date": new Date().toLocaleDateString(),
      "Total Customers": filteredCustomers.length,
      "Owners": ownerCount,
      "Non-Owners": nonOwnerCount,
      "Active": activeCount,
      "Inactive": inactiveCount,
      "Owner Rate": filteredCustomers.length > 0 
        ? `${((ownerCount / filteredCustomers.length) * 100).toFixed(1)}%` 
        : "0%",
      "Active Rate": filteredCustomers.length > 0 
        ? `${((activeCount / filteredCustomers.length) * 100).toFixed(1)}%` 
        : "0%"
    }];

    const ws = XLSX.utils.json_to_sheet(summaryData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Summary");
    
    const date = new Date();
    const fileName = `customers_summary_${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // ✅ Filters
  const filteredCustomers = customers.filter((c) => {
    const matchHouseType = filterHouseType
      ? c.houseType?._id === filterHouseType
      : true;

    const matchHouseNo = filterHouseNo
      ? String(c.houseNo || "")
          .toLowerCase()
          .includes(filterHouseNo.toLowerCase())
      : true;

    const matchZone = filterZone
      ? (c.zone?.name || "")
          .toLowerCase()
          .includes(filterZone.toLowerCase())
      : true;

    const matchCollector = filterCollector
      ? (c.collector?.fname || "")
          .toLowerCase()
          .includes(filterCollector.toLowerCase()) ||
        (c.collector?.lname || "")
          .toLowerCase()
          .includes(filterCollector.toLowerCase())
      : true;

    const matchStatus = filterStatus
      ? c.status === filterStatus
      : true;

    const matchOwner = filterOwner
      ? (filterOwner === "yes" ? c.isOwner === true : c.isOwner === false)
      : true;

    return matchHouseType && matchHouseNo && matchZone && matchCollector && matchStatus && matchOwner;
  });

  const ownerCount = filteredCustomers.filter(c => c.isOwner === true).length;
  const nonOwnerCount = filteredCustomers.filter(c => c.isOwner === false).length;
  const activeCount = filteredCustomers.filter(c => c.status === "active").length;
  const inactiveCount = filteredCustomers.filter(c => c.status === "inactive").length;

  if (loading) return <div className="loading">Loading customers...</div>;

  return (
    <div className="customers-container">
      {/* Header with Export Buttons */}
      <div className="header-section">
        <h1 className="page-title">Customers List</h1>
        <div className="action-buttons">
          <button className="summary-btn" onClick={exportSummary}>
            <FileText size={16} />
            Summary Report
          </button>
          <button className="export-btn" onClick={exportToExcel}>
            <Download size={16} />
            Export to Excel
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <select
          className="filter-select"
          value={filterHouseType}
          onChange={(e) => setFilterHouseType(e.target.value)}
        >
          <option value="">All House Types</option>
          {houseTypes.map((h) => (
            <option key={h._id} value={h._id}>
              {h.name}
            </option>
          ))}
        </select>

        <input
          type="text"
          className="filter-input"
          placeholder="Search by House No"
          value={filterHouseNo}
          onChange={(e) => setFilterHouseNo(e.target.value)}
        />

        <input
          type="text"
          className="filter-input"
          placeholder="Search by Zone"
          value={filterZone}
          onChange={(e) => setFilterZone(e.target.value)}
        />

        <input
          type="text"
          className="filter-input"
          placeholder="Search by Collector"
          value={filterCollector}
          onChange={(e) => setFilterCollector(e.target.value)}
        />

        <select
          className="filter-select"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>

        <select
          className="filter-select"
          value={filterOwner}
          onChange={(e) => setFilterOwner(e.target.value)}
        >
          <option value="">All Owners</option>
          <option value="yes">Owner (Yes)</option>
          <option value="no">Non-Owner (No)</option>
        </select>
      </div>

      {/* Stats Cards - Updated to show both Owner and Status stats */}
      <div className="stats-grid">
        <div className="stat-card total">
          <div className="stat-value">{filteredCustomers.length}</div>
          <div className="stat-label">Total Customers</div>
        </div>
        <div className="stat-card owner">
          <div className="stat-value">{ownerCount}</div>
          <div className="stat-label">Owners</div>
        </div>
        <div className="stat-card non-owner">
          <div className="stat-value">{nonOwnerCount}</div>
          <div className="stat-label">Non-Owners</div>
        </div>
        <div className="stat-card active">
          <div className="stat-value">{activeCount}</div>
          <div className="stat-label">Active</div>
        </div>
        <div className="stat-card inactive">
          <div className="stat-value">{inactiveCount}</div>
          <div className="stat-label">Inactive</div>
        </div>
        <div className="stat-card rate">
          <div className="stat-value">
            {filteredCustomers.length > 0 
              ? `${((activeCount / filteredCustomers.length) * 100).toFixed(1)}%` 
              : "0%"}
          </div>
          <div className="stat-label">Active Rate</div>
        </div>
      </div>

      {/* Customers Table */}
      <div className="table-container">
        <table className="customers-table">
          <thead>
            <tr>
              <th>House No</th>
              <th>Customer Name</th>
              <th>Phone</th>
              <th>Rate</th>
              <th>House Type</th>
              <th>Zone</th>
              <th>Collector</th>
              <th>Owner</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCustomers.length === 0 ? (
              <tr>
                <td colSpan="10" className="no-data">
                  No customers found
                </td>
              </tr>
            ) : (
              filteredCustomers.map((c) => (
                <tr key={c._id} className={c.status === 'inactive' ? 'inactive-row' : ''}>
                  <td>D.W-{c.houseNo}</td>
                  <td>{c.customerName}</td>
                  <td>{c.phone}</td>
                  <td>${c.rate}</td>
                  <td>{c.houseType?.name || "N/A"}</td>
                  <td>{c.zone?.name || "N/A"}</td>
                  <td>
                    {c.collector ? `${c.collector.fname || ""} ${c.collector.lname || ""}`.trim() : "N/A"}
                  </td>
                  <td>
                    <span className={`badge ${c.isOwner ? 'badge-yes' : 'badge-no'}`}>
                      {c.isOwner ? "Yes" : "No"}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${c.status === 'active' ? 'badge-active' : 'badge-inactive'}`}>
                      {c.status || "active"}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons-cell">
                      <button
                        onClick={() => navigate(`/customers/view/${c._id}`)}
                        className="icon-btn view"
                        title="View"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        onClick={() => navigate(`/customers/edit/${c._id}`)}
                        className="icon-btn edit"
                        title="Edit"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => toggleStatus(c._id, c.status)}
                        className={`icon-btn ${c.status === 'active' ? 'deactivate' : 'activate'}`}
                        title={c.status === 'active' ? 'Deactivate' : 'Activate'}
                      >
                        {c.status === 'active' ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                      </button>
                      <button
                        onClick={() => handleDelete(c._id)}
                        className="icon-btn delete"
                        title="Delete Permanently"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Customers;